// Eskom PARCC — AI-vs-human agreement per criterion.
//
// The POC's headline question: when the engine proposed a verdict for a
// criterion, did the reviewer's FINAL call match it? Both sides speak the same
// vocabulary (CES_STATUS_OPTIONS) and are keyed by the criterion's `seq`, so this
// is a seq-join — but three things make it easy to get subtly wrong, and each is
// deliberate here:
//   · A criterion counts ONLY when both sides have decided it. An undecided
//     reviewer is "pending", never a disagreement — otherwise the rate would
//     punish the engine for work the human has not done yet.
//   · Holistic findings (seq null) have no criterion to join on and are excluded.
//   · "Agreement" is about the compliance CALL, so the default compares at the
//     register level (Compliant / Possible-IE / Housekeeping / Policy / N-A).
//     `raw` mode compares the exact CES status instead.
//
// Pure — no hooks, no IO — so it unit-tests directly, like the rest of domain/.
import { cesMapStatusToRegister } from './ces';
import type { AiProposal } from '../lib/adminApi';

export interface CriterionAgreement {
  seq: number;
  aiVerdict: string;
  humanVerdict: string;
  agree: boolean;
}

export interface AgreementResult {
  /** Criterion proposals in scope (non-holistic) — the checklist size. */
  total: number;
  /** Criteria decided on BOTH sides — the denominator of the rate. */
  compared: number;
  agreed: number;
  disagreed: number;
  /** agreed / compared, or null when nothing is comparable yet. */
  agreementRate: number | null;
  /** The engine proposed, the reviewer has not decided this criterion. */
  pendingHuman: number;
  /** The reviewer decided, but the engine produced no verdict for it. */
  pendingAi: number;
  byCriterion: CriterionAgreement[];
}

export interface AgreementTotals {
  transactions: number;
  compared: number;
  agreed: number;
  disagreed: number;
  agreementRate: number | null;
}

/** Roll per-transaction agreement up to one overall rate — the aggregate the AI
 *  Assurance report leads with. The rate is over CRITERIA (compared), not a mean
 *  of per-transaction rates, so a 33-criterion review counts more than a 2. */
export function sumAgreement(results: readonly AgreementResult[]): AgreementTotals {
  let compared = 0;
  let agreed = 0;
  for (const r of results) {
    compared += r.compared;
    agreed += r.agreed;
  }
  return {
    transactions: results.length,
    compared,
    agreed,
    disagreed: compared - agreed,
    agreementRate: compared > 0 ? agreed / compared : null,
  };
}

const decided = (s: string | null | undefined): boolean =>
  typeof s === 'string' && s.trim() !== '';

/**
 * Compare the engine's proposed verdicts against the reviewer's saved statuses.
 *
 * @param aiProposals the run's proposals (holistic ones are ignored)
 * @param cesRows     the reviewer's per-criterion rows ({ seq, status })
 * @param opts.mode   'register' (default, compliance-call level) or 'raw'
 */
export function computeAgreement(
  aiProposals: readonly AiProposal[],
  cesRows: readonly { seq: number; status: string }[],
  opts: { mode?: 'register' | 'raw' } = {},
): AgreementResult {
  const raw = opts.mode === 'raw';
  const humanBySeq = new Map(cesRows.map((r) => [r.seq, r.status]));
  const criteria = aiProposals.filter((p) => p.seq !== null && !p.holistic);

  let compared = 0;
  let agreed = 0;
  let pendingHuman = 0;
  let pendingAi = 0;
  const byCriterion: CriterionAgreement[] = [];

  for (const p of criteria) {
    const seq = p.seq as number;
    const ai = p.verdict;
    const human = humanBySeq.get(seq);
    if (!decided(ai)) {
      pendingAi += 1;
      continue;
    }
    if (!decided(human)) {
      pendingHuman += 1;
      continue;
    }
    const agree = raw
      ? ai === human
      : cesMapStatusToRegister(ai) === cesMapStatusToRegister(human as string);
    compared += 1;
    if (agree) agreed += 1;
    byCriterion.push({ seq, aiVerdict: ai, humanVerdict: human as string, agree });
  }

  return {
    total: criteria.length,
    compared,
    agreed,
    disagreed: compared - agreed,
    agreementRate: compared > 0 ? agreed / compared : null,
    pendingHuman,
    pendingAi,
    byCriterion,
  };
}
